// /src/components/FAQManagement.js

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import api from '../api/axios';
import { 
  FaQuestionCircle, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaSave, 
  FaSpinner,
  FaTimes,
  FaExclamationTriangle,
  FaBuilding,
  FaCheck
} from 'react-icons/fa';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';

const PropertySelector = ({ properties, selectedProperty, onSelect, isLoading }) => (
  <Card className="bg-gray-800 border-gray-700">
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div className="w-full">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Select Property
          </label>
          {isLoading ? (
            <div className="flex items-center justify-center py-2">
              <FaSpinner className="animate-spin text-indigo-500" />
            </div>
          ) : (
            <div className="flex items-center space-x-2">
              <select
                value={selectedProperty || ''}
                onChange={(e) => onSelect(e.target.value)}
                className="w-full md:w-64 px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                        text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Choose a property</option>
                {properties.map((property) => (
                  <option key={property._id} value={property._id}>
                    {property.name}
                  </option>
                ))}
              </select>
              {selectedProperty && (
                <button
                  onClick={() => onSelect('')}
                  className="p-2 text-gray-400 hover:text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <FaTimes />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const FAQItem = ({ 
  faq, 
  onEdit, 
  onDelete, 
  isEditing, 
  onSave, 
  onCancel,
  editedQuestion, 
  setEditedQuestion, 
  editedAnswer, 
  setEditedAnswer,
  isSaving 
}) => (
  <Card className="bg-gray-800 border-gray-700 hover:border-gray-600 transition-all duration-300">
    <CardContent className="p-6">
      {isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Question
            </label>
            <input
              type="text"
              value={editedQuestion}
              onChange={(e) => setEditedQuestion(e.target.value)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                       text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Answer
            </label>
            <textarea
              value={editedAnswer}
              onChange={(e) => setEditedAnswer(e.target.value)}
              rows="3"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                       text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
          </div>
          <div className="flex justify-end space-x-2">
            <button
              onClick={onCancel}
              disabled={isSaving}
              className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 
                       transition-colors duration-200 flex items-center"
            >
              <FaTimes className="mr-1" /> Cancel
            </button>
            <button
              onClick={onSave}
              disabled={isSaving}
              className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                       transition-colors duration-200 flex items-center"
            >
              {isSaving ? (
                <>
                  <FaSpinner className="animate-spin mr-1" /> Saving...
                </>
              ) : (
                <>
                  <FaCheck className="mr-1" /> Save
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center">
              <FaQuestionCircle className="text-indigo-400 mr-2" />
              <h3 className="text-lg font-semibold text-white">{faq.question}</h3>
            </div>
            <p className="mt-2 text-gray-400 ml-7">{faq.answer}</p>
          </div>
          <div className="flex space-x-2 ml-4">
            <button
              onClick={() => onEdit(faq)}
              className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-gray-700 
                     rounded-lg transition-colors"
              title="Edit FAQ"
            >
              <FaEdit />
            </button>
            <button
              onClick={() => onDelete(faq)}
              className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700 
                     rounded-lg transition-colors"
              title="Delete FAQ"
            >
              <FaTrash />
            </button>
          </div>
        </div>
      )}
    </CardContent>
  </Card>
);

const DeleteConfirmDialog = ({ onConfirm, onCancel }) => (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
    <Card className="w-full max-w-md bg-gray-800 border-gray-700">
      <CardContent className="p-6">
        <div className="flex items-center mb-4">
          <FaExclamationTriangle className="text-yellow-400 mr-2 text-xl" />
          <h3 className="text-lg font-semibold text-white">Delete FAQ</h3>
        </div>
        <p className="text-gray-300 mb-6">
          Are you sure you want to delete this FAQ? This action cannot be undone.
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 
                   transition-colors duration-200"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 
                   transition-colors duration-200"
          >
            Delete
          </button>
        </div>
      </CardContent>
    </Card>
  </div>
);

const FAQManagement = () => {
  const { propertyId } = useParams(); // Get propertyId from URL params
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [faqs, setFaqs] = useState([]);
  const [isLoadingProperties, setIsLoadingProperties] = useState(true);
  const [isLoadingFAQs, setIsLoadingFAQs] = useState(false);
  const [error, setError] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState('');
  const [editedAnswer, setEditedAnswer] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingFaq, setDeletingFaq] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch properties on component mount
  useEffect(() => {
    fetchProperties();
  }, []);

  useEffect(() => {
    if (properties.length > 0) {
      if (propertyId) {
        // If propertyId is in URL, select it
        const property = properties.find(p => p._id === propertyId);
        if (property) {
          setSelectedProperty(propertyId);
        }
      } else if (!selectedProperty) {
        // If no propertyId in URL and no selection, select first property
        setSelectedProperty(properties[0]._id);
      }
    }
  }, [properties, propertyId]);

  // Fetch FAQs when property is selected
  useEffect(() => {
    if (selectedProperty) {
      fetchFAQs();
    } else {
      setFaqs([]);
    }
  }, [selectedProperty]);

  // Fetch properties
  const fetchProperties = async () => {
    setIsLoadingProperties(true);
    setError(null);
    try {
      const response = await api.get('/property/user/current', {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      const allProperties = [...response.data.owned, ...response.data.managed];
      setProperties(allProperties);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
      setError('Failed to load properties');
    } finally {
      setIsLoadingProperties(false);
    }
  };

  // Fetch FAQs
  const fetchFAQs = async () => {
    if (!selectedProperty) return;
    setIsLoadingFAQs(true);
    setError(null);
    try {
      const response = await api.get(`/faq/${selectedProperty}`, {
        headers: { 'x-auth-token': localStorage.getItem('token') }
      });
      setFaqs(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      setError('Failed to load FAQs');
    } finally {
      setIsLoadingFAQs(false);
    }
  };

  // Handle property selection
  const handlePropertySelect = (propertyId) => {
    setSelectedProperty(propertyId);
    if (propertyId) {
      navigate(`/faqs/${propertyId}`);
    } else {
      navigate('/faqs');
    }
    setEditingFaq(null);
    setEditedQuestion('');
    setEditedAnswer('');
    setError(null);
  };

  // Handle adding new FAQ
  const handleAddFAQ = () => {
    setEditingFaq({});
    setEditedQuestion('');
    setEditedAnswer('');
  };

  // Handle editing FAQ
  const handleEdit = (faq) => {
    setEditingFaq(faq);
    setEditedQuestion(faq.question || '');
    setEditedAnswer(faq.answer || '');
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingFaq(null);
    setEditedQuestion('');
    setEditedAnswer('');
  };

  // Handle saving FAQ
  const handleSave = async () => {
    if (!editedQuestion.trim() || !editedAnswer.trim()) {
      setError('Question and answer are required');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const newFaq = {
        question: editedQuestion.trim(),
        answer: editedAnswer.trim()
      };

      const updatedFaqs = editingFaq._id
        ? faqs.map(f => f._id === editingFaq._id ? newFaq : f)
        : [...faqs, newFaq];

      await api.post(
        '/faq',
        { 
          propertyId: selectedProperty, 
          questions: updatedFaqs 
        },
        { 
          headers: { 'x-auth-token': localStorage.getItem('token') } 
        }
      );

      setFaqs(updatedFaqs);
      setEditingFaq(null);
      setEditedQuestion('');
      setEditedAnswer('');
    } catch (error) {
      console.error('Failed to save FAQ:', error);
      setError('Failed to save FAQ');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle deleting FAQ
  const handleDelete = async () => {
    if (!deletingFaq) return;

    try {
      const updatedFaqs = faqs.filter(f => f !== deletingFaq);
      
      await api.post(
        '/faq',
        { 
          propertyId: selectedProperty, 
          questions: updatedFaqs 
        },
        { 
          headers: { 'x-auth-token': localStorage.getItem('token') } 
        }
      );

      setFaqs(updatedFaqs);
      setDeletingFaq(null);
      setShowDeleteConfirm(false);
    } catch (error) {
      console.error('Failed to delete FAQ:', error);
      setError('Failed to delete FAQ');
    }
  };

  return (
    <div className="flex-1 space-y-8 p-8 pt-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white">FAQ Management</h2>
          <p className="mt-2 text-gray-400">Manage frequently asked questions for your properties</p>
        </div>
        {selectedProperty && !editingFaq && (
          <button
            onClick={handleAddFAQ}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                     transition-colors duration-200 flex items-center"
          >
            <FaPlus className="mr-2" />
            Add FAQ
          </button>
        )}
      </div>

      {/* Property Selector */}
      <PropertySelector
        properties={properties}
        selectedProperty={selectedProperty}
        onSelect={handlePropertySelect}
        isLoading={isLoadingProperties}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-center">
          <FaExclamationTriangle className="text-red-500 mr-2" />
          <span className="text-red-500">{error}</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-red-500 hover:text-red-400"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Main Content */}
      <div className="space-y-4">
        {isLoadingFAQs ? (
          <div className="flex items-center justify-center h-32">
            <FaSpinner className="animate-spin text-indigo-500 text-2xl" />
          </div>
        ) : !selectedProperty ? (
          <Card className="bg-gray-800 border-gray-700">
            <CardContent className="p-6 text-center">
              <FaBuilding className="mx-auto text-4xl text-gray-600 mb-4" />
              <p className="text-gray-400">Select a property to manage its FAQs</p>
            </CardContent>
          </Card>
      ) : faqs.length === 0 && !editingFaq ? (
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-6 text-center">
            <FaQuestionCircle className="mx-auto text-4xl text-gray-600 mb-4" />
            <p className="text-gray-400">No FAQs yet. Click the Add FAQ button to create one.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* FAQ List */}
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              faq={faq}
              onEdit={handleEdit}
              onDelete={() => {
                setDeletingFaq(faq);
                setShowDeleteConfirm(true);
              }}
              isEditing={editingFaq === faq}
              onSave={handleSave}
              onCancel={handleCancelEdit}
              editedQuestion={editedQuestion}
              setEditedQuestion={setEditedQuestion}
              editedAnswer={editedAnswer}
              setEditedAnswer={setEditedAnswer}
              isSaving={isSaving}
            />
          ))}

          {/* New FAQ Form */}
          {editingFaq && !editingFaq._id && (
            <Card className="bg-gray-800 border-gray-700 border-2 border-indigo-500/50">
              <CardHeader>
                <CardTitle>New FAQ</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Question
                  </label>
                  <input
                    type="text"
                    value={editedQuestion}
                    onChange={(e) => setEditedQuestion(e.target.value)}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                             text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter the question"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Answer
                  </label>
                  <textarea
                    value={editedAnswer}
                    onChange={(e) => setEditedAnswer(e.target.value)}
                    rows="3"
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                             text-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter the answer"
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={handleCancelEdit}
                    className="px-3 py-1.5 bg-gray-600 text-white rounded-lg hover:bg-gray-700 
                             transition-colors duration-200 flex items-center"
                    disabled={isSaving}
                  >
                    <FaTimes className="mr-1" /> Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    className="px-3 py-1.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 
                             transition-colors duration-200 flex items-center"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <>
                        <FaSpinner className="animate-spin mr-1" /> Saving...
                      </>
                    ) : (
                      <>
                        <FaCheck className="mr-1" /> Save
                      </>
                    )}
                  </button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>

    {/* Delete Confirmation Dialog */}
    {showDeleteConfirm && (
      <DeleteConfirmDialog
        onConfirm={handleDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeletingFaq(null);
        }}
      />
    )}
  </div>
);
};

export default FAQManagement;